import moment from 'moment';
import "moment-timezone";
import React, { useEffect, useState } from "react";
import 'react-block-ui/style.css';
import { Button } from 'react-bootstrap';
import { Link } from "react-router-dom";
import Alert from 'react-s-alert';
import { useDebouncedCallback } from 'use-debounce';
import PageableTable from '../../Components/PageableTable/PageableTable';
import StandardFormModal from "../../Components/StandardFormModal/StandardFormModal";
import UsersService from '../../utils/UsersService';
import { formats } from '../../variables/variables';
import UserNewForm from "./UserNewForm";
import { userRoles } from './utility';

const Users = () => {
	const [userEditModal, setUserEditModal] = useState({ display: false, model: {} })

	const [recordState, setRecordState] = useState({ data: [], currentPage: 1, totalMatches: 0, totalPages: 0, perPage: 50, filter: { textFilter: '' } })
	const [setTextFilter] = useDebouncedCallback((value) => setRecordState({ ...recordState, filter: { ...recordState.filter, currentPage: 1, textFilter: value } }), 500)

	const getData =  async () => {
		const response = await UsersService.index({ textFilter: recordState.filter.textFilter, page: recordState.currentPage, per: recordState.perPage })
		setRecordState({ ...recordState, data: response.results, totalMatches: response.total_count, totalPages: response.total_pages })
   }

	const onPageChange = (currentPage) => { setRecordState({ ...recordState, currentPage: currentPage }); }

	useEffect(() => { getData() }, [recordState.currentPage, recordState.perPage, recordState.filter]);

	const editUser = (record) => {
		const title = record.id ? 'Edit User' : 'Create a New User'
		setUserEditModal({ display: true, title: title, model: record })
	}

	const userUpdated = (user) => {
    setUserEditModal({ ...userEditModal, display: false })
    const current = [...recordState.data];
    const idx = current.findIndex((r) => r.id === user.id);
    if (idx !== -1) {
      current.splice(idx, 1, user);
      setRecordState({...recordState, data: current })
    }
  }

  const updateUserLockout = (record, lockout) => {
    UsersService.update(record.id, { is_locked_out: lockout }).then((response) => {
      userUpdated(response);
      Alert.success(`User updated, now ${response.is_locked_out ? 'Locked Out' : 'Active'}`);
    })
  };

	const columns = [
    {
      header: "",
      render: (record) => (
        <>
          {record.is_locked_out ? (
            <i
              className="fas fa-lock-alt text-danger"
              style={{ cursor: "pointer" }}
              onClick={() => {
                updateUserLockout(record, false);
                record.is_locked_out = false;
              }}
            ></i>
          ) : (
            <i
              className="fas fa-unlock-alt text-info"
              style={{ cursor: "pointer" }}
              onClick={() => {
                updateUserLockout(record, true);
                record.is_locked_out = true;
              }}
            ></i>
          )}
        </>
      ),
    },

    {
      header: "Name",
      render: (record) => {
        const name =
          record.first_name.length > 0 && record.last_name.length > 0
            ? [record.first_name, record.last_name].join(" ")
            : record.last_name + record.first_name;

        return (
          <span>
            <Link to={`/m1/users/${record.id}`} style={{ marginRight: 30 }}>
              {name === "" ? "unknown" : name}
            </Link>
          </span>
        );
      },
    },
    {
      header: "Email",
      render: (record) => record.email,
    },
    {
      header: "Phone",
      render: (record) => (
        <span style={{ whiteSpace: "nowrap" }}>{record.phone}</span>
      ),
    },

    {
      header: "Schedule",
      render: (record) => (
        <Link to={`/m1/users/${record.id}/schedule`}>edit</Link>
      ),
    },

    {
      header: "Roles",
      render: (record) => {
        return record.roles.map((r, index) => (
          <span key={index} className="badge badge-info mr-1 mb-1">
            {userRoles[r]}
          </span>
        ));
      },
    },
    {
      header: "Last Active",
      render: (record) => (
        <span>
          {record.last_activity_at ? (
            <>{moment(record.last_activity_at).format(formats.fullDateTime)}</>
          ) : (
            "n/a"
          )}
        </span>
      ),
    },
  ];

	return (
    <div>
      <h1 className="first-title mb-0">Users</h1>
      <div className="mb-2">
        <Button bsStyle="link" onClick={() => editUser({})} style={{marginRight: 25}}>
          Setup a New User
        </Button>

        <Link to='/m1/users/schedules'>View All Schedules</Link>
      </div>

      <PageableTable
        pagingState={recordState}
        columns={columns}
        setTextFilter={setTextFilter}
        onPageChange={onPageChange}
      ></PageableTable>

      <StandardFormModal
        title={userEditModal.title}
        showModal={userEditModal.display}
        body={() => (
          <UserNewForm
            model={userEditModal.model}
            onSubmit={userUpdated}
            onCancelSubmit={() =>
              setUserEditModal({ ...userEditModal, display: false })
            }
          />
        )}
        onCloseModal={() =>
          setUserEditModal({ ...userEditModal, display: false })
        }
      />
    </div>
  );
}

export default Users
